import React, { useState } from 'react';
import { getProductImageURL } from '@noths/polaris-client-utils';
import Image from 'next/image';

import {
  NextButton,
  PreviousButton,
} from 'src/components/atoms/ImageNavigationButtons/ImageNavigationButtons';
import { ImageNumberLabel } from 'src/components/atoms/ImageNumberLabel/ImageNumberLabel';
import type { ProductImagesState } from 'src/components/organisms/ProductImagesCarousel/types';
import { useProductImagesCarousel } from 'src/components/organisms/ProductImagesCarousel/useProductImagesCarousel';
import type { CatServiceAPIImage } from 'src/services/cat-service-api/types/CatServiceAPIImage';
import * as styles from './ProductOptionsDrawerImages.styles';

export const PRODUCT_OPTIONS_DRAWER_IMAGES_ID = 'product-options-drawer-images';
export const SECTION_LABEL = 'Product images';

export interface ProductOptionsDrawerImagesProps {
  images: CatServiceAPIImage[];
}

export const ProductOptionsDrawerImages = ({ images }: ProductOptionsDrawerImagesProps) => {
  const totalImages = images.length;
  const [currentImage, setCurrentImage] = useState(0);
  const [navigatedBy, setNavigatedBy] = useState<ProductImagesState['navigatedBy']>(null);

  const [carouselContainerRef, carouselItemRefs] = useProductImagesCarousel(
    (entry) => {
      const imageIndex = Number((entry.target as HTMLElement).dataset.itemIndex);
      if (currentImage !== imageIndex) {
        setCurrentImage(imageIndex);
        setNavigatedBy('swipe');
      }
    },
    currentImage,
    navigatedBy,
    totalImages,
  );

  const goToImage = (imageIndex: number) => {
    setCurrentImage(imageIndex);
    setNavigatedBy('click');
  };

  return (
    <section aria-label={SECTION_LABEL} css={styles.stickyContainer}>
      <div css={styles.carouselWrapper}>
        <div css={styles.carousel} id={PRODUCT_OPTIONS_DRAWER_IMAGES_ID} ref={carouselContainerRef}>
          {images.map(({ href: src, title }, i) => (
            <div
              css={styles.slide}
              data-item-index={i}
              key={src}
              ref={(node) => {
                carouselItemRefs.current[i] = node as HTMLElement;
              }}
            >
              <Image
                alt={`${title}, ${i + 1} of ${totalImages}`}
                css={styles.image}
                fill={true}
                priority={i === 0}
                src={getProductImageURL(src, 'normal')}
                unoptimized
              />
            </div>
          ))}
        </div>
        {totalImages > 1 && (
          <>
            <PreviousButton
              controls={PRODUCT_OPTIONS_DRAWER_IMAGES_ID}
              onClick={() => goToImage(currentImage === 0 ? totalImages - 1 : currentImage - 1)}
            />
            <NextButton
              controls={PRODUCT_OPTIONS_DRAWER_IMAGES_ID}
              onClick={() => goToImage(currentImage === totalImages - 1 ? 0 : currentImage + 1)}
            />
            <ImageNumberLabel currentImage={currentImage + 1} totalImages={totalImages} />
          </>
        )}
      </div>
    </section>
  );
};
